'use client';

import type React from 'react';

import type { Content, ImageField, LinkField } from '@prismicio/client';
import type { SliceComponentProps } from '@prismicio/react';
import cx from 'classnames';
import { Button, Icon, Text } from 'components/@tedui';
import ContentContainer from 'components/content-container';
import { Honeypot } from 'components/footer/Honeypot';
import Image from 'components/image';
import { Link } from 'components/router';
import { getSystemLanguage } from 'lib/analytics/mixpanel-helpers';
import { useIsMobile } from 'lib/hooks/useIsBreakpointWidth';
import { MASTER_LIST } from 'lib/hooks/useSailthru/const';
import { getOptinDate } from 'lib/hooks/useSailthru/utils';
import { isValidEmail } from 'lib/util';
import { AnimatePresence, motion } from 'motion/react';
import { useState, type JSX } from 'react';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1
    }
  }
};

const headerVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.4, 0.25, 1]
    }
  }
};

const contentVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.4, 0.25, 1],
      staggerChildren: 0.1
    }
  }
};

const eventCardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.7,
      ease: [0.25, 0.4, 0.25, 1]
    }
  }
};

// Form animations
const formVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.4, 0.25, 1]
    }
  }
};

const successVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    transition: {
      duration: 0.3
    }
  }
};

const errorShakeVariants = {
  shake: {
    x: [-10, 10, -10, 10, 0],
    transition: {
      duration: 0.5
    }
  }
};

/**
 * Props for `TopicEvents`.
 */
export type TopicEventsProps = SliceComponentProps<Content.TopicEventsSlice>;

/**
 * Component for "TopicEvents" Slices.
 */
const TopicEvents = ({ slice }: TopicEventsProps): JSX.Element => {
  const [email, setEmail] = useState('');
  const [honeypotChecked, setHoneypotChecked] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isMobile } = useIsMobile({
    breakPointType: 'tui'
  });

  const isTwoEventsVariation = slice.variation === 'twoEvents';
  const isEventAndNewsletterVariation =
    slice.variation === 'eventAndNewsletter';

  const handleNewsletterSubmit = () => {
    const isEmailValid = isValidEmail(email);
    if (!isEmailValid) {
      setIsValid(false);
      return;
    }

    if (honeypotChecked) return;

    if (window.Sailthru === undefined) {
      console.error('Sailthru is not defined');
      return;
    }

    if (slice.variation !== 'eventAndNewsletter') return;

    setIsSubmitting(true);

    const vars: { [key: string]: string } = {};

    // Add newsletter subscription variables
    if (slice.primary.newsletter_list_name) {
      vars[`${slice.primary.newsletter_list_name}_subscriber`] = 'true';
      vars[`${slice.primary.newsletter_list_name}_optin_date`] = getOptinDate();

      if (slice.primary.newsletter_source) {
        vars[`${slice.primary.newsletter_list_name}_source`] = slice.primary
          .newsletter_source_value as string;
      }
    }

    // Add additional optin date value if specified
    if (slice.primary.newsletter_optin_date_value) {
      vars[slice.primary.newsletter_optin_date_value as string] =
        getOptinDate();
    }

    window.Sailthru.integration('userSignUp', {
      email,
      lists: {
        ...MASTER_LIST,
        ...(slice.primary.newsletter_list_name && {
          [slice.primary.newsletter_list_name]: 1
        })
      },
      vars,
      source: 'web',
      onSuccess: () => {
        setEmail('');
        setShowSuccess(true);
        setIsSubmitting(false);
        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      },
      onError: () => {
        console.error('Error subscribing to newsletter');
        setIsSubmitting(false);
      }
    });
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const isEmailValid = isValidEmail(value);
    setIsValid(isEmailValid);
    setEmail(value);
  };

  const renderEventCard = (
    eventLabel?: string | null,
    eventImage?: ImageField,
    eventTitle?: string | null,
    eventDate?: string | null,
    eventDescription?: string | null,
    ctaText?: string | null,
    ctaLink?: LinkField,
    isMainEvent = false
  ) => (
    <motion.div
      className={cx(
        'flex h-full flex-col rounded-sm bg-textSecondary-onLight p-4 md-tui:gap-2 md-tui:rounded-none md-tui:pt-2',
        {
          'lg-tui:col-span-1': isMainEvent
        }
      )}
      variants={eventCardVariants}
      initial="rest"
      whileHover="hover"
    >
      <motion.div>
        {eventLabel && (
          <Text
            useNewTextStyles
            color={{
              isOnLightSurface: false,
              override: '#828282'
            }}
            variant="label"
            weight="font-semibold"
            UNSAFE_className="tracking-[0.48px] leading-[150%] hidden md-tui:block"
          >
            {eventLabel}
          </Text>
        )}
        <div className="flex flex-1 flex-col gap-6 md-tui:flex-row">
          {eventImage?.url && (
            <div className="shrink-0">
              <motion.div className="relative aspect-square max-w-full overflow-hidden rounded-sm">
                <Image
                  src={eventImage.url}
                  width={200}
                  height={200}
                  className="object-cover transition-transform duration-700 ease-out [@media(max-width:767px)]:size-full"
                  alt={eventTitle || 'Event image'}
                />
              </motion.div>
            </div>
          )}
          <div className="flex flex-1 flex-col justify-between gap-3">
            <div className="flex flex-col gap-3">
              <div
                className={cx('flex flex-col', {
                  'gap-2': !isMobile
                })}
              >
                {eventTitle && (
                  <Text
                    useNewTextStyles
                    color={{
                      isOnLightSurface: false
                    }}
                    variant={isMobile ? 'header4' : 'header3'}
                    weight="font-semibold"
                  >
                    {eventTitle}
                  </Text>
                )}
                {eventDate && (
                  <Text
                    useNewTextStyles
                    color={{
                      isOnLightSurface: false
                    }}
                    variant={isMobile ? 'header4' : 'subheader1'}
                    {...(isMobile && {
                      weight: 'font-semibold'
                    })}
                  >
                    {eventDate}
                  </Text>
                )}
              </div>
              {eventDescription && (
                <Text
                  useNewTextStyles
                  color={{
                    isOnLightSurface: false
                  }}
                  variant="body2"
                >
                  {eventDescription}
                </Text>
              )}
            </div>
            {ctaText && ctaLink && 'url' in ctaLink && ctaLink.url && (
              <div className="mt-auto">
                <a
                  href={ctaLink.url}
                  className="flex items-center text-white underline underline-offset-4 transition-all duration-200 hover:no-underline"
                  target={'target' in ctaLink ? ctaLink.target : '_self'}
                  rel="noopener noreferrer"
                >
                  <Text
                    useNewTextStyles
                    color={{
                      isOnLightSurface: false
                    }}
                    variant={isMobile ? 'link2' : 'link1'}
                  >
                    {ctaText}
                  </Text>
                </a>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  const renderNewsletterCard = () => {
    if (slice.variation !== 'eventAndNewsletter') return null;

    return (
      <motion.div
        className="flex h-full flex-col gap-6"
        variants={eventCardVariants}
      >
        <motion.div className="flex flex-col gap-2" variants={formVariants}>
          {slice.primary.newsletter_title && (
            <Text
              useNewTextStyles
              color={{
                isOnLightSurface: false
              }}
              variant="unset"
              UNSAFE_className="flex-1 text-white text-tui-6xl leading-[110%] -tracking-[1.28px] md-tui:text-tui-4xl md-tui:leading-[110%] md-tui:-tracking-[1.12px] xl-tui:text-tui-6xl xl-tui:leading-[110%] xl-tui:-tracking-[1.28px] font-semibold"
            >
              {slice.primary.newsletter_title}
            </Text>
          )}

          {slice.primary.newsletter_text && (
            <Text
              useNewTextStyles
              color={{
                isOnLightSurface: false
              }}
              variant="subheader1"
            >
              {slice.primary.newsletter_text}
            </Text>
          )}
        </motion.div>

        <div className="flex flex-1 flex-col justify-center gap-6">
          <AnimatePresence mode="wait">
            {showSuccess ? (
              <motion.div
                key="success"
                variants={successVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <Text
                  variant="unset"
                  UNSAFE_className="flex-1 text-white text-tui-6xl leading-[110%] -tracking-[1.28px] md-tui:text-tui-4xl md-tui:leading-[110%] md-tui:-tracking-[1.12px] xl-tui:text-tui-6xl xl-tui:leading-[110%] xl-tui:-tracking-[1.28px]"
                  useNewTextStyles
                >
                  Thanks for subscribing!
                </Text>
              </motion.div>
            ) : (
              <>
                <motion.div
                  key="form"
                  className="flex flex-col gap-4"
                  variants={formVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <motion.div
                    className="relative w-full"
                    variants={isValid ? {} : errorShakeVariants}
                    animate={isValid ? {} : 'shake'}
                  >
                    <div className="flex items-center gap-2">
                      <div className="relative w-full md-tui:w-auto lg-tui:w-full">
                        <input
                          onChange={handleEmailChange}
                          placeholder="What is your email?"
                          id="newsletter-email"
                          className={cx(
                            'flex h-12 w-full flex-1 border-thin bg-white py-3 pl-3 pr-12 text-sm transition-all duration-200 placeholder:text-gray-700 focus:outline-none md-tui:max-w-80 lg-tui:max-w-full',
                            {
                              'border-systemInfo-error-onLight focus:border-thin focus:border-systemInfo-error-onLight':
                                !isValid,
                              'border-systemInfo-success focus:border-systemInfo-success':
                                isValid && email.length > 0
                            }
                          )}
                          type="email"
                          value={email}
                        />

                        <AnimatePresence>
                          {!isValid && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              transition={{ duration: 0.2 }}
                            >
                              <Icon
                                iconName="alert-triangle"
                                className="absolute right-2 top-1/2 mr-2 hidden -translate-y-1/2 transform text-tui-xl text-systemInfo-error-onDark md-tui:block lg-tui:hidden"
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      <AnimatePresence>
                        {!isValid && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Icon
                              iconName="alert-triangle"
                              className="absolute right-2 top-1/2 mr-2 -translate-y-1/2 transform text-tui-xl text-systemInfo-error-onDark md-tui:hidden lg-tui:block"
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <Button
                        variant="primary"
                        text={
                          slice.primary.newsletter_cta_text || 'Subscribe Now'
                        }
                        disabled={isSubmitting || !email.trim()}
                        UNSAFE_className="md-tui:min-w-35 min-w-full !h-12 transition-all duration-200 hidden md-tui:flex lg-tui:hidden"
                        isFullWidth={false}
                        onClick={handleNewsletterSubmit}
                      />
                    </div>
                  </motion.div>

                  <AnimatePresence>
                    {!isValid && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Text
                          tag="p"
                          variant="caption"
                          UNSAFE_className="mt-1 ml-2"
                          color={{
                            color: 'error'
                          }}
                        >
                          <span aria-live="polite">
                            Please add a valid email address
                          </span>
                        </Text>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <Button
                    variant="primary"
                    text={slice.primary.newsletter_cta_text || 'Subscribe Now'}
                    disabled={isSubmitting || !email.trim()}
                    UNSAFE_className="md-tui:min-w-35 min-w-full !h-12 transition-all duration-200 md-tui:hidden lg-tui:flex"
                    isFullWidth={false}
                    onClick={handleNewsletterSubmit}
                  />
                </motion.div>

                <Text
                  useNewTextStyles
                  variant="body2"
                  tag="span"
                  color={{ isOnLightSurface: false }}
                >
                  By subscribing, you accept the terms of{' '}
                  <Link
                    href="/about/our-organization/our-policies-terms/privacy-policy"
                    className="underline transition-all duration-200 hover:no-underline"
                    onClick={() => {
                      mixpanel.track('global_footer_click', {
                        event_interaction_context: 'newsletters_privacy-policy',
                        system_language: getSystemLanguage()
                      });
                    }}
                  >
                    TED's Privacy Policy
                  </Link>
                </Text>
              </>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    );
  };

  return (
    <motion.section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <ContentContainer className="bg-black pb-16 pt-8 lg-tui:pb-20 lg-tui:pt-10">
        {isEventAndNewsletterVariation && (
          <div className="h-0">
            <Honeypot onGrab={setHoneypotChecked} />
          </div>
        )}
        <motion.div
          className="mb-6 h-px w-full bg-white"
          variants={headerVariants}
        />
        <motion.div className="mb-10 md-tui:mb-6" variants={headerVariants}>
          {/* Section Title */}
          {slice.primary.section_title && (
            <Text
              useNewTextStyles
              color={{
                isOnLightSurface: false
              }}
              variant="unset"
              UNSAFE_className="text-tui-8xl leading-[105%] -tracking-[2px]"
            >
              {slice.primary.section_title}
            </Text>
          )}
        </motion.div>

        {/* Two Events Variation */}
        {isTwoEventsVariation && slice.items && (
          <motion.div
            className="flex flex-col gap-6 lg-tui:flex-row lg-tui:items-stretch lg-tui:gap-10"
            variants={contentVariants}
          >
            {slice.items.map((item, index) => (
              <motion.div
                key={index}
                className="flex flex-1"
                variants={eventCardVariants}
              >
                {renderEventCard(
                  item.event_label,
                  item.event_image,
                  item.event_title,
                  item.event_date,
                  item.event_description,
                  item.cta_text,
                  item.cta_link
                )}
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Event + Newsletter Variation */}
        {isEventAndNewsletterVariation && (
          <motion.div
            className="flex flex-col gap-10 lg-tui:flex-row lg-tui:items-stretch"
            variants={contentVariants}
          >
            {/* Single Event */}
            <motion.div className="w-full" variants={eventCardVariants}>
              {slice.variation === 'eventAndNewsletter' &&
                renderEventCard(
                  slice.primary.event_label,
                  slice.primary.event_image,
                  slice.primary.event_title,
                  slice.primary.event_date,
                  slice.primary.event_description,
                  slice.primary.event_cta_text,
                  slice.primary.event_cta_link,
                  true
                )}
            </motion.div>
            {/* Newsletter Signup */}
            <motion.div className="w-full" variants={eventCardVariants}>
              {renderNewsletterCard()}
            </motion.div>
          </motion.div>
        )}
      </ContentContainer>
    </motion.section>
  );
};

export default TopicEvents;
